import type { Size } from '../util';
import { Vector } from '../vector';
import * as Project from './coordinate.projection';
import type { GeometryCalibration } from './geometry.3d';
import * as GeoUtil from './geometry.util';

const PLANE_CENTER = new Vector(0, 0, 0);
const PLANE_NORMAL = new Vector(0, 0, -1);
const PLANE_AXIS_X = new Vector(1, 0, 0);
const RADIAL_ITERATIONS = 10;

/** In order to not back update older measurements, we need to flip the sign to keep the sign the same
 * as it was when this function as incorrectly directed, all calibrations uploaded from IKE field 1.0.18 and up
 * will have three parameters, and hence no sign flip
 */
function getRadialCenterCorrectionSign(cameraCalibration: GeometryCalibration) {
	if (cameraCalibration.distortion.length === 2) {
		return -1;
	}
	return 1;
}
export function ApplyRadialDistortion(
	cameraCalibration: GeometryCalibration,
	resolution: Size,
	point_CI: Vector
): Vector {
	var distorted_IP = Project.CalibratedToImage(resolution, point_CI);
	var corrected_optical_IP = distorted_IP.clone();

	var corrected_IP = distorted_IP.clone();

	var sign = getRadialCenterCorrectionSign(cameraCalibration);
	var factor = 1;
	for (var i = 0; i < RADIAL_ITERATIONS; i++) {
		corrected_optical_IP.x = corrected_IP.x + cameraCalibration.center.x * factor * sign;
		corrected_optical_IP.y = corrected_IP.y + cameraCalibration.center.y * factor * sign;

		var biggestDim = Math.max(resolution.height, resolution.width);

		//assume the optical centre is the radial centre and calculate magnitude r2 from here
		var r2 =
			((corrected_optical_IP.x / factor) * (corrected_optical_IP.x / factor) +
				(corrected_optical_IP.y / factor) * (corrected_optical_IP.y / factor)) /
			(biggestDim * biggestDim);
		factor = calculateRadialCorrectionFactor(r2, cameraCalibration.distortion);

		distorted_IP.x = corrected_IP.x / factor;
		distorted_IP.y = corrected_IP.y / factor;
	}

	return Project.ImageToCalibrated(resolution, distorted_IP);
}

export function RemoveRadialDistortion(cameraCalibration: GeometryCalibration, resolution: Size, point_CI: Vector) {
	var corrected_IP = Project.CalibratedToImage(resolution, point_CI);

	var corrected_optical_IP = corrected_IP.clone();
	var sign = getRadialCenterCorrectionSign(cameraCalibration);
	corrected_optical_IP.x += cameraCalibration.center.x * sign;
	corrected_optical_IP.y += cameraCalibration.center.y * sign;

	var biggestDim = Math.max(resolution.height, resolution.width);
	//assume the optical centre is the radial centre and calculate magnitude r2 from here
	var r2 =
		(corrected_optical_IP.x * corrected_optical_IP.x + corrected_optical_IP.y * corrected_optical_IP.y) /
		(biggestDim * biggestDim);
	var factor = calculateRadialCorrectionFactor(r2, cameraCalibration.distortion);

	corrected_IP.x = corrected_IP.x * factor;
	corrected_IP.y = corrected_IP.y * factor;

	return Project.ImageToCalibrated(resolution, corrected_IP);
}

function calculateRadialCorrectionFactor(r2, distortion) {
	var factor = 1;
	var rx = r2;
	for (var i = 0; i < distortion.length; i++) {
		var value = distortion[i];
		factor = factor + value * rx;
		rx = rx * r2;
	}

	return factor;
}

function calculatePlane(rotation) {
	var planeNormal = PLANE_NORMAL.rotate(rotation);
	var planeAxisX = PLANE_AXIS_X.rotate(rotation);
	var planeAxisY = planeNormal.cross(planeAxisX).multiply(-1);

	return {
		planeNormal,
		planeAxis: {
			x: planeAxisX,
			y: planeAxisY,
		},
	};
}

export function Distort(
	cameraCalibration: GeometryCalibration,
	resolution: Size,
	rotation: number[],
	point_CI: Vector
): Vector {
	var distorted_CI = ApplyRadialDistortion(cameraCalibration, resolution, point_CI);

	var centered_IP = Project.CalibratedToImage(resolution, distorted_CI);
	centered_IP.y = centered_IP.y * -1;

	var plane = calculatePlane(rotation);

	var intersect_IP = GeoUtil.linePlaneIntersection(
		{
			start: new Vector(0, 0, -cameraCalibration.focal),
			end: centered_IP,
		},
		PLANE_CENTER,
		plane.planeNormal
	);

	var image_IP = new Vector(intersect_IP.dot(plane.planeAxis.x), intersect_IP.dot(plane.planeAxis.y));

	image_IP.y = image_IP.y * -1;

	return Project.ImageToCalibrated(resolution, image_IP);
}

export function UnDistort(
	cameraCalibration: GeometryCalibration,
	resolution: Size,
	rotation: number[],
	point_CI: Vector
) {
	var point_IP = Project.CalibratedToImage(resolution, point_CI);
	point_IP.y = point_IP.y * -1;

	var rotated_IP = point_IP.rotate(rotation);

	rotated_IP.x = (cameraCalibration.focal * rotated_IP.x) / (rotated_IP.z + cameraCalibration.focal);
	rotated_IP.y = (-cameraCalibration.focal * rotated_IP.y) / (rotated_IP.z + cameraCalibration.focal);
	rotated_IP.z = 0;
	var rotated_CI = Project.ImageToCalibrated(resolution, rotated_IP);

	return RemoveRadialDistortion(cameraCalibration, resolution, rotated_CI);
}
